import { useEffect, useState, type ReactElement } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabaseClient';
import { SessionProvider } from './context/SessionContext';
import { ProjectsProvider } from './context/ProjectsContext';
import { UserProfileProvider, useUserProfile } from './context/UserProfileContext';
import WorkspacePage from './pages/Workspace';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import type { UserRole } from './types/api';

export const App = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setLoading(false);
    };

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });

    void init();

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  return (
    <BrowserRouter>
      <UserProfileProvider session={session}>
        <Routes>
          <Route path="/" element={<LandingPage session={session} />} />
          <Route path="/login" element={<LoginPage session={session} loading={loading} />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute session={session} loading={loading}>
                <SessionProvider value={{ session: session! }}>
                  <ProjectsProvider>
                    <WorkspacePage />
                  </ProjectsProvider>
                </SessionProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute session={session} loading={loading} allowedRoles={['admin']}>
                <SessionProvider value={{ session: session! }}>
                  <AdminPage />
                </SessionProvider>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </UserProfileProvider>
    </BrowserRouter>
  );
};

type ProtectedRouteProps = {
  session: Session | null;
  loading: boolean;
  children: ReactElement;
  allowedRoles?: UserRole[];
};

const ProtectedRoute = ({ session, loading, children, allowedRoles = ['admin', 'user'] }: ProtectedRouteProps) => {
  const { profile, loading: profileLoading } = useUserProfile();

  if (loading) {
    return <p className="muted">Booting Shanework…</p>;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (profileLoading) {
    return <p className="muted">Checking access…</p>;
  }

  if (!profile) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(profile.role)) {
    if (profile.role === 'pending') {
      return <Navigate to="/login?status=pending" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default App;
